import { selectHasApprovalFeature } from '~/organizations/selectors';

import { useAppSelector } from '~/store';
import { PublishingPost } from '~/post/types.generated';
import {
  RpcUpdate,
  PostAction,
  PostActionButtonType,
  PostActionLabel,
  PostActionType,
  PostActionTooltipMessage,
  UncheckedPostAction,
} from '~/post/types';
import { getPostValidationMessage } from '../../helpers';
import { usePostPermissions } from '../usePostPermissions';
import { PostEntity } from '../../PostEntity';
import {
  isValidPostAction,
  reducePostActionsByType,
  usePostActionsResponse,
} from './helpers';

export type usePostActionsProps = {
  post: PublishingPost | RpcUpdate;
  onRescheduleClick?: (args?: unknown) => unknown;
  onRequestApprovalClick?: (args?: unknown) => unknown;
  onRevertApprovalClick?: (args?: unknown) => unknown;
  onApproveClick?: (args?: unknown) => unknown;
  onMovePostToDraftsClick?: (args?: unknown) => unknown;
  onSharePostNowClick?: (args?: unknown) => unknown;
};

/**
 * Return an object containing arrays of valid post actions
 * that can be performed in the given context.
 *
 * It will exclude actions based on the conditions provided below per action,
 * as well as whether a callback for the given action has been provided.
 *
 * The callback filter will likely be deprecated, but is currently still
 * useful while we work with different post types that have different
 * state management.
 *
 * The provided post is validated and the validation is used when calculating
 * valid posts.
 * It's important to note whether an action should be disabled by validation or not.
 * eg:
 * - Approval should be blocked by validation as it deals with scheduling posts.
 * - Moving a post to drafts does not have to be blocked by validation.
 *
 * @returns {
 *    singleActions: An array of PostActions that will be rendered separately
 *    stackedActions: An array of PostActions that will be rendered in a single, stacked Select Button
 * }
 */
export const usePostActions = ({
  post,
  onRescheduleClick,
  onRequestApprovalClick,
  onRevertApprovalClick,
  onApproveClick,
  onMovePostToDraftsClick,
  onSharePostNowClick,
}: usePostActionsProps): usePostActionsResponse => {
  const hasApprovalFeature = useAppSelector(selectHasApprovalFeature);
  const { isManager, isPostOwner } = usePostPermissions(post);
  const validationMessage = getPostValidationMessage({
    post,
    userNeedsApproval: hasApprovalFeature && !isManager && isPostOwner,
  });

  const isScheduled = PostEntity.isScheduled(post);
  const isPastDue = PostEntity.isPastDue(post);
  const isDraft = PostEntity.isDraft(post);
  const isSent = PostEntity.isSent(post);
  const needsApproval = PostEntity.needsApproval(post);

  const postActions: UncheckedPostAction[] = [
    {
      id: PostActionType.RESCHEDULE,
      title: PostActionLabel.RESCHEDULE,
      type: PostActionButtonType.SINGLE,
      tooltip: PostActionTooltipMessage.RESCHEDULE,
      callback: onRescheduleClick,
      condition:
        (isManager || isPostOwner) && isScheduled && isPastDue && !isSent,
      disabled: false,
    },
    {
      id: PostActionType.ADD_TO_QUEUE,
      title: PostActionLabel.ADD_TO_QUEUE,
      type: PostActionButtonType.SINGLE,
      tooltip: validationMessage || PostActionTooltipMessage.ADD_TO_QUEUE,
      callback: onApproveClick,
      condition:
        isManager && isDraft && !isScheduled && !needsApproval && !isSent,
      disabled: !!validationMessage,
    },
    {
      id: PostActionType.SCHEDULE_POST,
      title: PostActionLabel.SCHEDULE_POST,
      type: PostActionButtonType.SINGLE,
      tooltip: validationMessage || PostActionTooltipMessage.SCHEDULE_POST,
      callback: onApproveClick,
      condition:
        isManager &&
        isScheduled &&
        isDraft &&
        !needsApproval &&
        !isPastDue &&
        !isSent,
      disabled: !!validationMessage,
    },
    {
      id: PostActionType.REQUEST_APPROVAL,
      title: PostActionLabel.REQUEST_APPROVAL,
      type: PostActionButtonType.SINGLE,
      tooltip: validationMessage || PostActionTooltipMessage.REQUEST_APPROVAL,
      callback: onRequestApprovalClick,
      condition:
        !isManager &&
        isPostOwner &&
        hasApprovalFeature &&
        isDraft &&
        !needsApproval &&
        !isPastDue &&
        !isSent,
      disabled: !!validationMessage,
    },
    {
      id: PostActionType.APPROVE,
      title: PostActionLabel.APPROVE,
      type: PostActionButtonType.SINGLE,
      tooltip: validationMessage || PostActionTooltipMessage.APPROVE,
      callback: onApproveClick,
      condition:
        isManager &&
        hasApprovalFeature &&
        isDraft &&
        needsApproval &&
        !isPastDue &&
        !isSent,
      disabled: !!validationMessage,
    },
    {
      id: PostActionType.REJECT,
      title: PostActionLabel.REJECT,
      type: PostActionButtonType.SINGLE,
      tooltip: PostActionTooltipMessage.REJECT,
      callback: onRevertApprovalClick,
      condition:
        isManager && hasApprovalFeature && isDraft && needsApproval && !isSent,
      disabled: false,
    },
    {
      id: PostActionType.REVERT_TO_DRAFT,
      title: PostActionLabel.REVERT_TO_DRAFT,
      type: PostActionButtonType.SINGLE,
      tooltip: validationMessage || PostActionTooltipMessage.REVERT_TO_DRAFT,
      callback: onRevertApprovalClick,
      condition:
        !isManager &&
        isPostOwner &&
        hasApprovalFeature &&
        isDraft &&
        needsApproval &&
        !isSent,
      disabled: !!validationMessage,
    },
    {
      id: PostActionType.MOVE_POST_TO_DRAFTS,
      title: PostActionLabel.MOVE_POST_TO_DRAFTS,
      type: PostActionButtonType.STACKED,
      tooltip: PostActionTooltipMessage.MOVE_POST_TO_DRAFTS,
      callback: onMovePostToDraftsClick,
      condition: isManager && !isDraft && !isSent,
      disabled: false,
    },
    {
      id: PostActionType.SHARE_NOW,
      title: PostActionLabel.SHARE_NOW,
      type: PostActionButtonType.STACKED,
      tooltip: validationMessage || PostActionTooltipMessage.SHARE_NOW,
      callback: onSharePostNowClick,
      condition: isManager && !isDraft && !isSent,
      disabled: !!validationMessage,
    },
  ];

  // Filter out actions with failed conditions
  // and reduce actions to expected structure
  return postActions.filter(isValidPostAction).reduce(reducePostActionsByType, {
    singleActions: [] as PostAction[],
    stackedActions: [] as PostAction[],
  });
};
